<template>
  <v-container fluid class="mt-0 pt-0">
    <v-dialog
      v-model="showDatePicker"
      ref="dialog"
      width="290px"
      persistent
    >
      <v-date-picker
        v-model="datePickerDate"
        no-title
        @change="closeDatePicker"
      ></v-date-picker>
    </v-dialog>
    <v-toolbar flat pa-0>
      <v-menu
        v-model="showMealMenu"
        :close-on-content-click="false"
        v-if="loading || Object.keys(mealsByDate).length>0 || ccMealsInProduction.length>0"
      >
        <template v-slot:activator="{ on,attrs }">
          <v-btn
            outlined
            color="primary"
            v-bind="attrs"
            v-on="on"
            :loading="loading"
            :disabled="taskStarted"
          >
            Select Meal
          </v-btn>
        </template>
        <v-card min-width="800">
          <v-card-text>
            Note that meal count shown here for Low Carb (LC) includes the Diabetes number.
            On the plating page the count is just for LC meals.
          </v-card-text>
          <v-card-text class="py-0 d-flex align-center flex-nowrap">
            <v-btn
              large
              rounded
              class="mr-3 px-6 text-capitalize flex-shrink-0"
              v-for="tod of tods"
              :key="tod"
              :outlined="todSelection !== tod"
              :color="todSelection === tod ? 'primary' : ''"
              @click="selectTod(tod)"
            >
              {{ getTimeOfDay(tod) }}
              <span class="ml-2">({{ todMealsToPlate[tod] || 0 }})</span>
            </v-btn>
            <v-spacer/>
            <v-text-field
              v-model="mealSearch"
              label="Search meals"
              append-icon="mdi-magnify"
              single-line
              hide-details
              clearable
              class="ml-4"
              style="max-width: 300px"
            />
          </v-card-text>
          <v-card-text>
            <v-row>
              <v-col v-for="date of datesWithMeals" v-bind:key="`${date}-stream`">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-title><h3>{{ dateFormatted(date) }}</h3></v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    dense
                    v-for="(meal, i) in mealsWithOrders[date]"
                    :key="i"
                    @click="selectMeal(meal.id,date)"
                  >
                    <v-list-item-content class="text-capitalize">
                      <div :style="isMealPlated(meal.id) ? 'text-decoration: line-through':''">
                        <span v-if="getMealRack(meal.id)" class="red--text font-weight-bold">{{
                            getMealRack(meal.id)
                          }} </span>
                        <b v-if="meal.production_priority">P{{ meal.production_priority }}</b>
                        {{ mealNameShort(meal) }}
                        {{ getMealCount(meal.id) }}CT
                        <v-icon fab color="green" v-if="isComplete(meal.id)">mdi-check</v-icon>
                        <template v-if="isAllergyComplete(meal.id)">
                          <v-chip small color="green">A
                            <v-icon fab>mdi-check</v-icon>
                          </v-chip>
                        </template>
                      </div>
                      <!-- the label alone does not say what was matched -->
                      <div v-if="mealSearch" class="text-none text-caption">{{ meal.name }}</div>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-if="chefsChoiceMeals.length > 0" class="pt-0">
            <h3>Chef's Choice</h3>
            <v-row dense>
              <v-col cols="4" v-for="meal of chefsChoiceMeals" :key="`cc-${meal.id}`">
                <v-list-item dense @click="selectMeal(meal.id)">
                  <v-list-item-content>
                    <div :style="isMealPlated(meal.id) ? 'text-decoration: line-through':''">
                      <span v-if="getMealRack(meal.id)" class="red--text font-weight-bold">{{
                          getMealRack(meal.id)
                        }} </span>
                      <b v-if="meal.production_priority">P{{ meal.production_priority }}</b>
                      {{ meal.name }}
                      {{ getMealCount(meal.id) }}CT
                      <v-icon fab color="green" v-if="isComplete(meal.id)">mdi-check</v-icon>
                      <template v-if="isAllergyComplete(meal.id)">
                        <v-chip small color="green">A
                          <v-icon fab>mdi-check</v-icon>
                        </v-chip>
                      </template>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-menu>
      <template v-if="totalPlatingTime">
        <v-spacer/>
        <v-btn :disabled="!taskStarted" :loading="loading" color="success" class="ml-2" outlined
               @click="stopTask({complete:true})" v-if="task">
          Mark Done
        </v-btn>
        <v-spacer/>
        <v-btn :loading="loading" color="warning" class="ml-2" outlined @click="stopTask" v-if="task && taskStarted">
          Pause Timer
        </v-btn>
        <v-btn :loading="loading" class="ml-2" outlined @click="startTask" v-if="task && taskStopped">
          Start Timer
        </v-btn>
      </template>
      <v-spacer/>
      <ProductionSelector
        :dates.sync="dates"
        :production.sync="production"
        :production-filter="['local', chefsChoiceProduction]"
      />
      <v-spacer/>
      <v-btn :outlined="!showRestrictions" color="warning" @click="showRestrictions=!showRestrictions">Restrictions
      </v-btn>
      <v-btn icon @click="SET_HIDE_TOP_NAV({hideTopNav:!hideTopNav})">
        <v-icon>mdi-fullscreen</v-icon>
      </v-btn>
    </v-toolbar>
    <RunningTasksDialog
      v-model="showRunningTasksDialog"
      :running-tasks="currentRunningTasks"
    />
    <v-dialog v-model="showTeamMemberDialog" max-width="600">
      <v-card>
        <v-card-title>Select Person</v-card-title>
        <v-card-text>
          <v-chip-group
            v-model="selectedTeamMemberId"
            column
            active-class=""
            @change="showTeamMemberDialog=false"
          >
            <v-chip
              v-for="teamMember of activeTeamMembers"
              v-bind:key="teamMember.id"
              :value="teamMember.id"
              :color="selectedTeamMemberId === teamMember.id ? 'primary' : ''"
              :outlined="selectedTeamMemberId !== teamMember.id"
            >
              {{ teamMember.name }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="showTeamMemberDialog=false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row
      class=""
      :style="warningColor ? `border: 10px solid ${warningColor}` : ''"
    >
      <v-col :cols="12">
        <v-row dense class="">
          <v-col cols="4" v-if="meal">
            <div :class="`${production}-production`">
              <p style="font-weight: bolder; font-size: 25px" class="plating-tv-title">
                <v-chip outlined v-if="meal.production_priority">P{{ meal.production_priority }}</v-chip>
                {{ meal.name }}
              </p>
              <p style="font-weight: bolder; font-size: 25px" class="text-capitalize ">
                {{ getDietName(meal.diet) }} | {{ getTimeOfDay(meal.tod) }} |
                <v-chip v-if="isChefsChoiceSelected" outlined color="green">CC LINE</v-chip>
                <template v-else>{{ dateFormatted(meal.date, {formatString: 'ddd'}) }}</template>
                <template v-if="production==='ship'">
                  |
                  <v-chip outlined color="blue">SHIP PRODUCTION</v-chip>
                </template>
              </p>
            </div>
            <v-img v-if="imageUrl && imageZoom" @click="imageZoom=false" v-bind:key="imageUrl"
                   style=""
                   :src="platingImageUrl"/>
            <!--                  <img v-if="imageUrl && imageZoom" @click="imageZoom=false" v-bind:key="imageUrl"-->
            <!--                       :style="`width: 100%; height:100%; background: url(${imageUrl.replace('/small','')}/rotate=90); background-size: 150%;  background-position: center;`">-->
            <v-img @click="imageZoom=true" v-if="imageUrl && !imageZoom" :src="imageUrl"/>
          </v-col>
          <v-col cols="8" v-if="meal && !loading">
            <v-form v-if="!totalPlatingTime">
              <v-row dense>
                <v-col cols="4">
                  <v-text-field
                    :label="`How many people on ${platingLine} team?`"
                    v-model="teamCount"
                    type="number"
                  />
                </v-col>
                <v-col cols="1" class="text-center text-h6 pt-4">OR</v-col>
                <v-col cols="7" class="pt-4">
                  <v-btn
                    large
                    :outlined="!selectedTeamMember"
                    :color="selectedTeamMember ? 'primary' : ''"
                    @click="showTeamMemberDialog=true"
                  >
                    {{ selectedTeamMember ? selectedTeamMember.name : 'Select Person' }}
                  </v-btn>
                  <v-btn icon v-if="selectedTeamMember" @click="selectedTeamMemberId=null">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-btn :disabled="!(teamCount>0)" @click="calculateTime">Calculate Time</v-btn>
            </v-form>
            <template v-if="totalPlatingTime">
              <Plating
                hide-header
                hide-controls
                read-only
                :meal-id="mealId"
                :production="production"
                :date="selectedDate"
                :orders-in="ordersForSelectedMeal"
                :dates="dates"
                dense
                smaller
                :team-count="teamCount"
              />
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!--    <pre>-->
    <!--    task-->
    <!--      {{ task }}-->
    <!--    </pre>-->
  </v-container>

</template>

<script>
import Plating from "@/components/Plating";
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {dateFormatted, getMealImageUrl, getPlatingImageUrl} from "@/store/utils";
import urlState from "@/router/urlState";
import * as Sentry from "@sentry/vue";
import ProductionSelector from "@/components/ProductionSelector.vue";
import RunningTasksDialog from "@/components/RunningTasksDialog.vue";

const MAIN_LINE = 'Main';
const CC_LINE = 'CC';
// each plating line is a team member of its own, so tasks are timed against it
const lineTeamName = line => `${line} Line`;
const CHEFS_CHOICE_PRODUCTION = 'CC';
const CHEFS_CHOICE_DIET = 'Chefs Choice';

export default {
  name: "PlatingTV",
  components: {RunningTasksDialog, ProductionSelector, Plating},
  mixins: [urlState],
  props: {
    date: {},
    initialHideNav: {}
  },
  data() {
    return {
      team: 'plating',
      team_members: [],
      selectedTeamMemberId: null,
      meals: {},
      ccMeals: [],
      orders: {},
      mealId: null,
      meal: null,
      drawer: null,
      dates: [],
      showDatePicker: null,
      datePickerDate: null,
      showRestrictions: false,
      showMealMenu: false,
      showTeamMemberDialog: false,
      todSelection: null,
      mealSearch: '',
      selectedDate: null,
      loading: false,
      imageZoom: true,
      teamCount: null,
      totalPlatingTime: null,
      task: null,
      tasks: [],
      taskStarted: false,
      taskStopped: false,
      coldWarningTimer: null,
      coldDangerTimer: null,
      warningColor: '',
      complete: {},
      componentTaskMap: {},
      platingTaskMap: {},
      platingTaskMealMap: {},
      allergyPlatingTaskMealMap: {},
      intervals: [],
      timers: [],
      production: undefined,
      showRunningTasksDialog: false,
      fetchScheduled: false,

    }
  },
  unmounted() {
    console.log('removing listeners/intervals');
    this.$socket.removeAllListeners();
    console.log('clearing timers');
    this.intervals.forEach(id => clearInterval(id));
    this.timers.forEach(id => clearTimeout(id));
    this.intervals = [];
    this.timers = [];
  },
  async mounted() {
    this.syncToUrl({
      param: 'mealId', urlParam: 'meal', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'production', urlParam: 'production', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'showRestrictions', urlParam: 'restrictions', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'teamCount', urlParam: 'team', initFromRoute: true,
      parseCallback: (v) => v ? Number(v) : null
    });
    this.syncToUrl({
      param: 'todSelection', urlParam: 'tod', initFromRoute: true,
      parseCallback: (v) => v ? Number(v) : null
    });
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });

    if (this.initialHideNav) {
      this.SET_HIDE_TOP_NAV({hideTopNav: true});
    }
    this.$socket.on('task:started', (task) => {
      console.log('task started', task);
      this.startColdWarningTimers(task.elapsed);
      const index = this.tasks.findIndex(t => t.id === task.id);
      if (index > -1) {
        this.$set(this.tasks, index, task);
      } else {
        // console.warn('could not find started task', {task, tasks: this.tasks});
      }
      this.updateComponentTask(task);
      this.updatePlatingTask(task);
    });
    this.$socket.on('task:stopped', (task) => {
      console.log('task stopped', task);
      this.updateComponentTask(task);
      this.updatePlatingTask(task);
    });
    this.$socket.on('task:updated', (task) => {
      console.log('task updated', task);
      this.updateComponentTask(task);
      this.updatePlatingTask(task);
    });
    this.$socket.on('task:created', (task) => console.log('task created', task));
    this.loading = true;
    await this.$socket.emitPromise('members:fetch',
      {team: this.team},
      (result) => {
        const {error, teamMembers} = result;
        if (error) {
          console.warn(`members:fetch failed: ${error}`);
        } else {
          console.log('plating members:fetch result', teamMembers);
          this.team_members = teamMembers;
        }
      }
    );
    this.$nextTick(() => {
      if (this.date && this.production) {
        this.fetchData()
          .then(() => {
            this.loading = true;
            if (this.mealId) {
              return this.changeMeal(this.mealId);
            }
          })
          .then(() => {
            if (this.teamCount) {
              this.calculateTime();
              this.totalPlatingTime = true;
            }
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    })
  },
  computed: {
    ...mapState(['hideTopNav']),
    ...mapGetters(['getDietName', 'getTimeOfDay', 'getTimeOfDayNumber', 'getOrdersOnDate', 'getOrderSummaryForMeal']),    // meals(){
    imageUrl() {
      return this.meal && this.getMealImageUrl(this.meal);
    },
    platingImageUrl() {
      return getPlatingImageUrl(this.meal);
    },
    // the meals in production, before the time of day filter is applied
    mealsByDate() {
      const result = {};
      console.log('calc meals with orders', this.dates, this.meals, this.orders);
      for (const date of this.dates) {
        const mealsOnDate = this.meals[date];
        if (mealsOnDate && this.orders[date]) {
          result[date] = this.sortMeals(mealsOnDate
            .filter(m => this.orders[date].find(o => o.meal_id === m.id))
            .filter(({id}) => this.getMealCount(id) > 0));
        }
      }
      return result;
    },
    mealsWithOrders() {
      const result = {};
      for (const [date, meals] of Object.entries(this.mealsByDate)) {
        result[date] = meals.filter(meal => this.isMealShown(meal));
      }
      return result;
    },
    mealsInProduction() {
      return [...Object.values(this.mealsByDate).flat(), ...this.ccMealsInProduction];
    },
    // only offer the times of day which are actually in production
    tods() {
      return [...new Set(this.mealsInProduction.map(meal => this.mealTod(meal)))].sort((a, b) => a - b);
    },
    // how many meals are still to be plated for each time of day
    todMealsToPlate() {
      const toPlate = {};
      this.mealsInProduction
        .filter(meal => !this.isMealPlated(meal.id))
        .forEach(meal => {
          const tod = this.mealTod(meal);
          toPlate[tod] = (toPlate[tod] || 0) + 1;
        });
      return toPlate;
    },
    datesWithMeals() {
      return this.dates.filter(date => (this.mealsWithOrders[date] || []).length > 0);
    },
    chefsChoiceProduction() {
      return CHEFS_CHOICE_PRODUCTION;
    },
    // tasks generated for anything but the local production carry a prefix
    taskReferencePrefix() {
      return this.production === 'local' ? '' : `PROD-${this.production}`;
    },
    // chef's choice meals are not menued by a production day, so they are
    // listed on their own rather than in the per-day columns
    ccMealsInProduction() {
      return this.sortMeals(this.ccMeals.filter(meal => this.getMealCount(meal.id) > 0));
    },
    chefsChoiceMeals() {
      return this.ccMealsInProduction.filter(meal => this.isMealShown(meal));
    },
    isChefsChoiceSelected() {
      return this.production === CHEFS_CHOICE_PRODUCTION
        || (!!this.meal && this.getDietName(this.meal.diet) === CHEFS_CHOICE_DIET);
    },
    platingLine() {
      return this.isChefsChoiceSelected ? 'CC' : 'Main';
    },
    platingLineName() {
      return lineTeamName(this.platingLine);
    },
    platingLineTeam() {
      return this.team_members.find(tm => tm.name === this.platingLineName);
    },
    // the lines are team members themselves, so leave them out of the picker
    activeTeamMembers() {
      return this.team_members
        .filter(tm => !tm.inactive && ![MAIN_LINE, CC_LINE].map(lineTeamName).includes(tm.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    selectedTeamMember() {
      return this.team_members.find(tm => tm.id === this.selectedTeamMemberId);
    },
    // a chosen person takes the place of the line for this task
    platingTeam() {
      return this.selectedTeamMember || this.platingLineTeam;
    },
    // chef's choice orders are spread over the whole production, so the
    // plating detail cannot look them up by a single day
    ordersForSelectedMeal() {
      if (!this.isChefsChoiceSelected) {
        return null;
      }
      return this.dates
        .flatMap(date => this.orders[date] || [])
        .filter(o => Number(o.meal_id) === Number(this.mealId));
    },
    currentRunningTasks() {
      const team = this.platingTeam;

      if (!team) {
        console.log('team not loaded');
        return [];
      }
      const tasks = Object.values(this.platingTaskMap);

      const isRunningTask = task => {
        const lastExecution = task.execution && task.execution[task.execution.length - 1];
        return lastExecution && lastExecution.stop === null;
      }
      const includesThisTeam = task => {
        const lastExecution = task.execution && task.execution[task.execution.length - 1];
        return !!lastExecution.team_members.find(tm => tm.id === teamMemberId);
      }

      const teamMemberId = team.id;
      const runningTasks = tasks.filter(isRunningTask);
      const tasksMatchingTeamMember = runningTasks.filter(includesThisTeam);
      console.log('findRunningTasksWithTeam  ' + teamMemberId, tasksMatchingTeamMember);
      return tasksMatchingTeamMember;
    }
  },
  watch: {
    selectedTeamMemberId(id) {
      if (id) {
        // one person plating is a team of one
        this.teamCount = 1;
      }
    },
    // a search whose only matches are in another time of day should not come
    // back empty - drop the filter rather than show nothing
    mealSearch() {
      const matches = this.mealsInProduction.filter(meal => this.matchesSearch(meal));
      if (this.todSelection && matches.length > 0 && !matches.some(meal => this.isSelectedTod(meal))) {
        console.log('clearing time of day filter, it hides every search result');
        this.todSelection = null;
      }
    },
    // a production cycle changes dates and production together, so fetch once
    dates: 'scheduleFetchData',
    production: 'scheduleFetchData',
    mealId(id) {
      this.changeMeal(id);
    },
    currentRunningTasks(tasks) {
      if (tasks.length === 0) {
        this.showRunningTasksDialog = false;
      }
    }
  },
  methods: {
    ...mapActions(['fetchMeals', 'fetchDiets', 'fetchMeal', 'fetchMealsById', 'fetchOrdersByDate']),
    ...mapMutations(['SET_HIDE_TOP_NAV']),
    dateFormatted,
    // meals without a time of day are plated with the extras
    mealTod(meal) {
      return this.getTimeOfDayNumber(this.getTimeOfDay(meal.tod));
    },
    selectTod(tod) {
      // selecting the time of day already shown clears the filter
      this.todSelection = this.todSelection === tod ? null : tod;
    },
    isSelectedTod(meal) {
      return !this.todSelection || this.todSelection === this.mealTod(meal);
    },
    matchesSearch(meal) {
      const search = (this.mealSearch || '').trim().toLowerCase();
      return !search || [meal.name, this.getDietName(meal.diet), this.mealNameShort(meal)]
        .join(' ')
        .toLowerCase()
        .includes(search);
    },
    isMealShown(meal) {
      return this.isSelectedTod(meal) && this.matchesSearch(meal);
    },
    sortMeals(meals) {
      meals.sort((m1, m2) => {
        const meal1Plated = this.isMealPlated(m1.id) && this.isAllergyComplete(m1.id);
        const meal2Plated = this.isMealPlated(m2.id) && this.isAllergyComplete(m2.id);
        if (meal1Plated && !meal2Plated) {
          return 1;
        }
        if (!meal1Plated && meal2Plated) {
          return -1;
        }

        const m1Racked = this.getMealRack(m1.id);
        const m2Racked = this.getMealRack(m2.id);
        if (m1Racked && !m2Racked) {
          return -1;
        }
        if (m2Racked && !m1Racked) {
          return 1;
        }
        const diff = this.getMealCount(m2.id) - this.getMealCount(m1.id);
        if (diff !== 0) {
          return diff;
        } else {
          return m1.name.localeCompare(m2.name);
        }
      });
      return meals;
    },
    changeMeal(id) {
      if (id) {
        if (this.meal && this.meal.id !== id) {
          // stop current task
          if (this.task) {
            console.log('stopping current task',this.task);
            this.stopTask({complete: true});
          }
        }

        return this.fetchMeal(id)
          .then(meal => {
            this.totalPlatingTime = false;
            this.meal = meal;
            // a chef's choice meal is not made for a production day
            this.selectedDate = this.isChefsChoiceSelected ? null : meal.date;
            if (this.currentRunningTasks.length > 0) {
              // need popup screen that lets them select if these tasks are done or paused.
              this.showRunningTasksDialog = true;
            }
          });
      } else {
        this.meal = null;
      }
    },
    startColdWarningTimers(elapsed = 0) {
      const coldWarningTime =
        (30 * 60 * 1000) // 30 mins
        // (1 * 60 * 1000) // 1 mins
        - (elapsed || 0); // minus any time already elapsed
      if (this.coldWarningTimer) {
        clearTimeout(this.coldWarningTimer);
      }
      if (coldWarningTime > elapsed) {
        this.coldWarningTimer = setTimeout(() => {
          this.warningColor = 'yellow';
          alert('COLD WARNING!\n30 minute food safe warning!  10 minutes of safe time left.');
        }, coldWarningTime);

        this.timers.push(this.coldWarningTimer);
      }

      const coldDangerTime =
        (40 * 60 * 1000) // 40 mins
        // (2 * 60 * 1000) // 1 mins
        - (elapsed || 0); // minus any time already elapsed
      if (this.coldDangerTimer) {
        clearTimeout(this.coldDangerTimer);
      }
      if (coldDangerTime > elapsed) {
        this.coldDangerTimer = setTimeout(() => {
          this.warningColor = 'red';
          alert('DANGER!\n40 minute food safe warning!');
        }, coldDangerTime);
        this.timers.push(this.coldDangerTimer);
      }


      this.warningColor = '';
      if (elapsed > coldWarningTime) {
        this.warningColor = 'yellow';
      }
      if (elapsed > coldDangerTime) {
        this.warningColor = 'red';
      }

      console.log('setting background', {elapsed, coldWarningTime, coldDangerTime, color: this.warningColor});
    },
    stopColdWarningTimers() {
      this.warningColor = '';
      if (this.coldWarningTimer) {
        clearTimeout(this.coldWarningTimer);
        this.coldWarningTimer = false;
      }
      if (this.coldDangerTimer) {
        clearTimeout(this.coldDangerTimer);
        this.coldDangerTimer = false;
      }
    },
    async getOrCreatePlatingLineTeam() {
      const name = this.platingLineName;
      if (this.platingLineTeam) {
        return this.platingLineTeam;
      }
      console.log('plating line not found, creating', name);
      try {
        const {teamMember} = await this.$socket.emitPromise('members:add', {name, team: this.team});
        this.team_members.push(teamMember);
        return teamMember;
      } catch (error) {
        console.warn(`could not create plating line ${name}`, error);
        return null;
      }
    },
    async startTask() {
      const {id} = this.task;
      const member = this.selectedTeamMember || await this.getOrCreatePlatingLineTeam();
      const team_members = member ? [member] : [];

      console.log('task:starting - ', {id, team_members});
      this.loading = true;
      this.$socket.emit('task:start', {id, team_members},
        (result) => {
          console.log('task:start - result', result);
          this.taskStarted = true;
          this.taskStopped = false;
          const {error} = result;
          this.loading = false;
          if (error) {
            if (error === 'already executing task') {
              // stop and restart
              this.loading = true;
              this.$socket.emit('task:stop', {id}, () => {
                this.startTask();
              });
            } else {
              console.warn('failed to start', result);
            }
            // alert(error);
          }
        });
    },
    stopTask({complete = false, note = `${this.teamCount} people`}) {
      const {id} = this.task;
      console.log('stop', 'task:stop', {id, complete, note});
      this.stopColdWarningTimers();
      this.loading = true;
      return this.$socket.emitPromise('task:stop', {id, complete, note}, (result) => {
        console.log('task:stop - result', result);
        this.taskStarted = false;
        this.taskStopped = true;
        this.loading = false;
      });
    },
    mealCountNoAllergy(mealId, date) {
      // chef's choice meals have no production day, so count the whole production
      const orders = date
        ? (this.orders[date] || []).filter(o => o.date === date)
        : this.dates.flatMap(d => this.orders[d] || []);
      console.log('count', orders.length, mealId, date);
      return orders
        .filter(o => o.meal_id == mealId)
        .filter(o => !o.allergyDetected)
        .reduce((sum, o) => sum + o.quantity, 0);
    },

    async getOrCreateTask(meal) {
      const count = this.mealCountNoAllergy(meal.id, this.selectedDate);
      const title = `M${meal.id} - ${meal.name} - T:${this.teamCount}, C:${count}`;
      const reference = `${this.taskReferencePrefix}PLATING-M${meal.id}-${this.dates.join('-')}`;
      // lookup by reference first, then try title
      const existing = this.tasks.find(t => t.reference === reference) || this.tasks.find(t => t.title === title);
      if (existing) {
        console.log('found existing task', existing);
        return existing;
      } else {
        const task = {};
        task.from = this.date;
        task.to = this.date;
        task.team = this.team;
        task.title = title;
        task.reference = reference;
        task.description = `${this.teamCount} people, ${count} meals`;
        console.log('creating task', task);
        return new Promise((resolve, reject) => {
          this.$socket.emit('task:create', task, response => {
            console.log('create task response', response);
            if (response.id) {
              resolve(response);
            } else {
              console.warn('failed to create plating task: ' + JSON.stringify(response));
              reject(new Error('failed to create plating task'));
            }
          });
        });
      }
    },
    async fetchComponentTasks() {
      const dates = this.dates;
      this.incomplete = {};
      this.componentTaskMap = {};
      return new Promise(resolve => {
        const team = 'components';
        this.$socket.emit('tasks:fetch',
          {dates, team, productions: [this.production]},
          (result) => {
            const {error, tasks} = result;
            if (error) {
              alert(`fetch tasks ${dates} failed: ${error}`);
            } else {
              console.log(`tasks:fetch team=${team} - ${dates} result`, result);
              tasks.forEach(task => {
                const {id} = task;
                this.componentTaskMap[id] = {...(this.componentTaskMap[id] || {}), ...task};
                this.updateComponentTask(task);
              });
            }
            resolve();
          }
        );
      });
    },
    scheduleFetchData() {
      if (this.fetchScheduled) {
        return;
      }
      this.fetchScheduled = true;
      this.$nextTick(() => {
        this.fetchScheduled = false;
        this.fetchData();
      });
    },
    async fetchData() {
      if (this.dates.length === 0 || !this.production) {
        console.log('dates and production not set yet', this.dates, this.production);
        return;
      }
      this.loading = true;
      this.totalPlatingTime = false;
      // this.dates = getProductionDays(this.date);
      this.meals = {};
      this.ccMeals = [];
      this.orders = {};
      this.mealId = null;
      return Promise
        .all([
          this.fetchDiets(),
          ...this.dates.map(date => this
            .fetchOrdersByDate({date: `${date}_${this.production}`})
            .then(orders => this.$set(this.orders, date, orders))),
          ...this.dates.map(date => this.fetchMeals(date).then(meals => this.$set(this.meals, date, meals))),
          this.fetchComponentTasks(),
          this.fetchPlatingTasks(),
        ])
        .then(() => this.fetchChefsChoiceMeals())
        .finally(() => {
          console.log('all done loading');
          this.loading = false;
        });
    },
    // chef's choice meals are not menued on a production day, so they cannot be
    // loaded by date - the plating tasks tell us which ones are in production
    async fetchChefsChoiceMeals() {
      const mealsByDate = this.dates.flatMap(date => this.meals[date] || []);
      const isChefsChoiceTask = ({source}) => source
        && !source.allergy
        && (this.production === CHEFS_CHOICE_PRODUCTION || source.dietName === CHEFS_CHOICE_DIET);
      const mealIds = [...new Set(this.tasks.filter(isChefsChoiceTask).map(t => t.source.mealId))]
        .filter(mealId => mealId && !mealsByDate.find(m => m.id === mealId));
      if (mealIds.length === 0) {
        this.ccMeals = [];
        return;
      }
      const meals = await this.fetchMealsById(mealIds);
      this.ccMeals = meals.filter(meal => meal);
      console.log('loaded chefs choice meals', this.ccMeals);
    },
    trunc(s, len) {
      return s.slice(0, len);
    },
    getMealImageUrl,
    closeDatePicker() {
      this.showDatePicker = false;
      this.meal = null;
      this.$nextTick(() => this.fetchData());
      this.$router.push({params: {date: this.datePickerDate}});
    },
    async calculateTime() {
      this.totalPlatingTime = true;
      this.task = await this.getOrCreateTask(this.meal);
      if (this.task) {
        this.startTask();
      }
    },
    selectMeal(mealId, date) {
      this.showMealMenu = false;
      const selectMeal = () => {
        this.mealId = mealId;
        this.selectedDate = date;
        this.taskStarted = false;
        this.taskStopped = false;
      };
      if (this.mealId === mealId) {
        this.mealId = null;
        this.$nextTick(selectMeal);
      } else {
        selectMeal();
      }
    },
    isComplete(mealId) {
      if (this.complete[mealId]) {
        return Object.values(this.complete[mealId]).every(t => t);
      } else {
        return false;
      }
    },
    isAllergyComplete(mealId) {
      return this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].allergyComplete;
    },
    getMealRack(mealId) {
      return this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].rack;
    },
    getMealCount(mealId) {
      return this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].mealCount;
    },
    isMealPlated(mealId) {
      return this.platingTaskMealMap[mealId] && this.platingTaskMealMap[mealId].complete;
    },
    updatePlatingTask(task) {
      const {id} = task;
      if (!this.platingTaskMap[id]) {
        // console.log('skipping, not in map', task);
        return;
      }
      const updatedTask = {...this.platingTaskMap[id], ...task};
      this.$set(this.platingTaskMap, id, updatedTask);
      task = updatedTask;
      if (task.source) {
        const mealId = task.source.mealId;
        if (task.source.allergy) {
          this.$set(this.allergyPlatingTaskMealMap, mealId, {...task.source, complete: task.complete});
        } else {
          this.$set(this.platingTaskMealMap, mealId, {...task.source, complete: task.complete});
        }
      } else {
        try {
          Sentry.setContext('task', task);
          Sentry.captureMessage('task did not have source')
        } catch (e) {
          console.warn('sentry capture failed', e);
        }
      }
      // console.log(`updating ${mealId} ac=${task.source.allergyComplete} r=${task.source.rack}`)
    },
    updateComponentTask(task) {
      const {id} = task;
      if (!this.componentTaskMap[id]) {
        // console.log('skipping, not in map', task);
        return;
      }
      task = this.componentTaskMap[id] = {...this.componentTaskMap[id], ...task};
      // console.log('updating task', task);
      const source = task.source;
      if (!source) {
        // console.log('no source, skipping', task);
        return;
      }
      const componentId = source.component && source.component.id;
      const componentName = source.component && source.component.name;
      if (componentName.match(/garnish/i)) {
        // console.log('skipping garnish', componentName);
        return;
      }
      const complete = task.complete;
      if (source && source.meals) {
        for (const meal of source.meals) {
          const {meal_id} = meal;
          this.$set(this.complete, meal_id, this.complete[meal_id] || {});
          this.$set(this.complete[meal_id], componentId, complete);
        }
      }
    },
    mealNameShort(meal) {
      const dietName = this.getDietName(meal.diet);

      function getInitials(str) {
        return str.split(' ').map(word => word[0]).join('');
      }

      let initials = getInitials(dietName).toUpperCase();
      if (initials === 'P') {
        initials = 'PB';
      }

      return `${initials}-` +
        `${this.trunc(this.getTimeOfDay(meal.tod), 1)}-` +
        `${dateFormatted(meal.date, {formatString: 'ddd'})}`
          .toUpperCase();
    },
    fetchPlatingTasks() {
      return new Promise(resolve => {
        const dates = this.dates;
        this.$socket.emit('tasks:fetch',
          {dates, team: this.team, productions: [this.production]},
          (result) => {
            const {error, tasks} = result;
            if (error) {
              console.warn(`fetch tasks ${dates} failed: ${error}`);
            } else {
              console.log(`tasks:fetch team=${this.team}- ${dates} result`, result);
              this.tasks = tasks;
              tasks.forEach(task => {
                const {id} = task;
                this.$set(this.platingTaskMap, id, {...(this.platingTaskMap[id] || {}), ...task});
                this.updatePlatingTask(task);
              });
            }
            resolve();
          }
        );
      });
    }
  }
}
</script>
